import { axiosInstance } from './apiCommon';

/**
 * The format a company's EPDs are migrated from into Dynamic Forms. Only the Simple Wizard migration exists; 'Compiler'
 * joins once the Compiler migration is built, and the requests then carry the source.
 */
export type CompanyMigrationSource = 'SimpleWizard';

/** What a migration run does (mirrors CompanyMigrationOperation in EPDServices.Domain/Model/Migration). */
export type CompanyMigrationOperation = 'DryRun' | 'Rehearsal' | 'Rollback';

/** State of a migration run (mirrors CompanyMigrationState in EPDServices.Domain/Model/Migration). */
export type CompanyMigrationState = 'Pending' | 'InProgress' | 'Succeeded' | 'Failed';

/** One migration run (mirrors CompanyMigrationRunModel in EPDServices.Application/Features/Migration). */
export interface CompanyMigrationRunModel {
  id: string;
  operation: CompanyMigrationOperation;
  state: CompanyMigrationState;
  mapperVersion?: string | null;
  requestedBy?: string | null;
  requestedAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  /** EPDs migrated (ready or ready with follow-ups); for a rollback, the copies removed. */
  migratedCount: number;
  /** EPDs that could not be migrated. */
  blockedCount: number;
  readyCount: number;
  /** Migrated EPDs whose owner completes some data in Dynamic Forms. */
  followUpEpdCount: number;
  /** EPDs with at least one mapping note. */
  notedEpdCount: number;
  versionCount: number;
  fileCount: number;
  failureReason?: string | null;
  /** A finished dry run can be deleted; rehearsals and rollbacks are kept. */
  canDelete: boolean;
}

/** Whether an EPD can be migrated (mirrors CompanyMigrationEpdOutcome in EPDServices.Domain/Model/Migration). */
export type CompanyMigrationEpdOutcome = 'Ready' | 'ReadyWithFollowUps' | 'Blocked';

/** How much an issue matters (mirrors CompanyMigrationIssueCategory). */
export type CompanyMigrationIssueCategory = 'Blocker' | 'FollowUp' | 'MappingNote';

/** What an issue is about (mirrors CompanyMigrationIssueCode). */
export type CompanyMigrationIssueCode =
  | 'MissingRegistrationNumber'
  | 'NoVersions'
  | 'MissingVersionNumber'
  | 'DuplicateVersionNumber'
  | 'MissingTextData'
  | 'UnresolvedDigitalVersion'
  | 'InvalidMegaJsonType'
  | 'TranslationFailed'
  | 'InsertFailed'
  | 'VerificationFailed'
  | 'CopyStructureMismatch'
  | 'StatusMismatch'
  | 'DashboardCardMismatch'
  | 'ContentMismatch'
  | 'FileMismatch'
  | 'RequiredValueMissing'
  | 'InvalidValue'
  | 'ValidationError'
  | 'SourceValueMissing'
  | 'LiveValueUsed'
  | 'AccountManagerUsed'
  | 'FirstVersionDateFallback'
  | 'DefaultTitleUsed'
  | 'ValueDropped'
  | 'ValueRefiled';

/** The outcome of one EPD in a run (mirrors CompanyMigrationEpdModel). */
export interface CompanyMigrationEpdModel {
  epdHeadId: string;
  registrationNumber?: number | null;
  title?: string | null;
  /** EPDStatusDisplayName as the Simple Wizard dashboard shows it. */
  simpleWizardStatus?: string | null;
  outcome: CompanyMigrationEpdOutcome;
  followUpCount: number;
  noteCount: number;
  blockerMessage?: string | null;
}

/** One finding about an EPD (mirrors CompanyMigrationIssueModel). */
export interface CompanyMigrationIssueModel {
  category: CompanyMigrationIssueCategory;
  code: CompanyMigrationIssueCode;
  versionNumber?: number | null;
  propertyId?: string | null;
  propertyLabel?: string | null;
  section?: string | null;
  /** The role that completes a follow-up: EpdOwner, Verifier, Administrator or LicenseeAdministrator. */
  fixedBy?: string | null;
  message: string;
}

/** The migration panel of a company; the action flags come from the server (mirrors CompanyMigrationsModel). */
export interface CompanyMigrationsModel {
  isEnabled: boolean;
  hasCopies: boolean;
  isBusy: boolean;
  canDryRun: boolean;
  canRehearse: boolean;
  canRollBack: boolean;
  runs: CompanyMigrationRunModel[];
}

/** One log row of a run (mirrors CompanyMigrationLogModel). */
export interface CompanyMigrationLogModel {
  createdAt: string;
  step: string;
  level: 'Info' | 'Warning' | 'Error';
  registrationNumber?: number | null;
  versionNumber?: number | null;
  message: string;
  detailsJson?: string | null;
}

const baseUrl = (companyId: string) => `/api/admin/v1/AdminCompanies/${encodeURIComponent(companyId)}/Migrations`;

/** System Administrator only. The runs of the company, newest first, and which actions are allowed now. */
export const getAdminCompanyMigrations = async (companyId: string): Promise<CompanyMigrationsModel> =>
  (await axiosInstance.get<CompanyMigrationsModel>(baseUrl(companyId))).data;

/**
 * System Administrator only. Queues a run; the company-migration job runs it. 400 (message in `detail`) when the
 * migration is off in this environment, a run is already queued, or the company's copies do not allow it.
 */
export const requestAdminCompanyMigration = async (
  companyId: string,
  operation: CompanyMigrationOperation
): Promise<string> => (await axiosInstance.post<string>(`${baseUrl(companyId)}/${operation}`)).data;

/** System Administrator only. Deletes a finished dry run with its results; 400 for any other run. */
export const deleteAdminCompanyMigrationRun = async (companyId: string, runId: string): Promise<void> => {
  await axiosInstance.delete(`${baseUrl(companyId)}/${encodeURIComponent(runId)}`);
};

/** System Administrator only. The log of one run, oldest first. */
export const getAdminCompanyMigrationLog = async (
  companyId: string,
  runId: string,
  skip = 0,
  take = 200
): Promise<CompanyMigrationLogModel[]> =>
  (
    await axiosInstance.get<CompanyMigrationLogModel[]>(`${baseUrl(companyId)}/${encodeURIComponent(runId)}/log`, {
      params: { skip, take },
    })
  ).data;

/** System Administrator only. The run's per-EPD outcomes, blocked first; optionally one outcome or one issue code. */
export const getAdminCompanyMigrationEpds = async (
  companyId: string,
  runId: string,
  filter: { outcome?: CompanyMigrationEpdOutcome; code?: CompanyMigrationIssueCode } = {}
): Promise<CompanyMigrationEpdModel[]> =>
  (
    await axiosInstance.get<CompanyMigrationEpdModel[]>(`${baseUrl(companyId)}/${encodeURIComponent(runId)}/epds`, {
      params: { ...filter, take: 500 },
    })
  ).data;

/** System Administrator only. Everything found for one EPD in a run. */
export const getAdminCompanyMigrationEpdIssues = async (
  companyId: string,
  runId: string,
  epdHeadId: string
): Promise<CompanyMigrationIssueModel[]> =>
  (
    await axiosInstance.get<CompanyMigrationIssueModel[]>(
      `${baseUrl(companyId)}/${encodeURIComponent(runId)}/epds/${encodeURIComponent(epdHeadId)}/issues`
    )
  ).data;

/** System Administrator only. Every issue of the run with its EPD, as a CSV file. */
export const downloadAdminCompanyMigrationIssues = async (companyId: string, runId: string): Promise<Blob> =>
  (
    await axiosInstance.get<Blob>(`${baseUrl(companyId)}/${encodeURIComponent(runId)}/issues/export`, {
      responseType: 'blob',
    })
  ).data;
